// Anatomia da tela de Integrações (/queue-integration) — mockup "Tela Aeros
// Dark - Integrações e Modais": chips de filtro, grade de cards de 3 colunas
// e o painel de documentação com abas.
import { makeStyles, alpha } from "@material-ui/core/styles";
import { AEROS, stateText } from "../../styles/aerosTokens";

export const MONO = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

// Paleta fixa do painel de código: escuro nos dois temas, para o exemplo
// ler-se igual em qualquer configuração.
export const CODE = Object.freeze({
  bg: "#0E1119",
  border: "rgba(255,255,255,0.08)",
  text: "#F1F2F6",
  key: "#22D3EE",
  muted: "#5B6376",
});

const useIntegrationPageStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const borderSoft = isLight ? theme.palette.divider : "rgba(255,255,255,0.06)";
  const borderCard = isLight ? theme.palette.divider : "rgba(255,255,255,0.07)";

  return {
    pageSubtitle: {
      fontSize: 12,
      color: theme.palette.text.secondary,
      lineHeight: 1.3,
      marginTop: 1,
    },

    // ---- chips de filtro ----
    filterRow: {
      display: "flex",
      gap: 8,
      alignItems: "center",
      flexWrap: "wrap",
      marginBottom: 22,
    },
    filterChip: {
      borderRadius: 99,
      padding: "5px 14px",
      fontSize: 12,
      fontWeight: 600,
      textTransform: "none",
      minWidth: 0,
      color: theme.palette.text.secondary,
      border: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.1)"}`,
      display: "flex",
      alignItems: "center",
      // O gap vai no `MuiButton-label`: é ele que envolve os filhos, então um
      // gap no botão não separa o rótulo da contagem.
      "& .MuiButton-label": { gap: 6 },
      "&:hover": { borderColor: alpha(theme.palette.primary.main, 0.4) },
    },
    filterChipActive: {
      color: stateText(theme, "#C08BFF", "purple"),
      backgroundColor: alpha(theme.palette.primary.main, 0.15),
      borderColor: "transparent",
      "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.22) },
    },
    filterCount: { fontSize: 10.5, opacity: 0.7, fontVariantNumeric: "tabular-nums" },

    // ---- grade de cards ----
    grid: {
      display: "grid",
      gridTemplateColumns: "repeat(3,minmax(0,1fr))",
      gap: 14,
      [theme.breakpoints.down("md")]: { gridTemplateColumns: "repeat(2,minmax(0,1fr))" },
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "minmax(0,1fr)" },
    },
    card: {
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${borderCard}`,
      borderRadius: 14,
      padding: 16,
      display: "flex",
      flexDirection: "column",
      gap: 12,
      minWidth: 0,
      transition: "border-color 120ms ease",
      "&:hover": { borderColor: alpha(theme.palette.primary.main, 0.4) },
    },
    cardHead: { display: "flex", alignItems: "center", gap: 10, minWidth: 0 },
    tile: {
      width: 40,
      height: 40,
      borderRadius: 11,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flex: "none",
      "& .MuiSvgIcon-root": { fontSize: 21 },
    },
    cardName: {
      fontSize: 13.5,
      fontWeight: 600,
      color: theme.palette.text.primary,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    cardMeta: {
      fontSize: 11.5,
      color: theme.palette.text.secondary,
      display: "flex",
      alignItems: "center",
      gap: 6,
      minWidth: 0,
    },
    cardType: { fontWeight: 600 },
    statusPill: {
      display: "inline-flex",
      alignItems: "center",
      gap: 5,
      fontSize: 10.5,
      fontWeight: 600,
      borderRadius: 99,
      padding: "2px 9px",
      flex: "none",
    },
    statusDot: { width: 6, height: 6, borderRadius: "50%" },
    endpoint: {
      fontFamily: MONO,
      fontSize: 11.5,
      color: theme.palette.text.secondary,
      backgroundColor: isLight ? theme.palette.aeros.surface2 : CODE.bg,
      border: `1px solid ${borderSoft}`,
      borderRadius: 8,
      padding: "7px 10px",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    endpointEmpty: { color: theme.palette.aeros.textTertiary, fontStyle: "italic" },
    linksRow: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      flexWrap: "wrap",
      fontSize: 11,
      color: theme.palette.text.secondary,
      "& > .MuiSvgIcon-root": {
        fontSize: 14,
        color: theme.palette.aeros.textTertiary,
        flex: "none",
      },
    },
    linkChip: {
      border: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.1)"}`,
      borderRadius: 6,
      padding: "2px 8px",
      fontWeight: 600,
      maxWidth: "100%",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    linkWarning: {
      color: stateText(theme, AEROS.warning, "warning"),
      fontWeight: 600,
    },
    cardFooter: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      paddingTop: 10,
      borderTop: `1px solid ${borderSoft}`,
    },
    cardHint: { fontSize: 11, color: theme.palette.aeros.textTertiary },
    cardActions: { marginLeft: "auto", display: "flex", gap: 5 },
    iconButton: {
      width: 28,
      height: 28,
      borderRadius: 8,
      padding: 0,
      border: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.08)"}`,
      color: theme.palette.text.secondary,
      "&:hover": { color: AEROS.cyan, borderColor: alpha(AEROS.cyan, 0.4) },
      "& .MuiSvgIcon-root": { fontSize: 15 },
    },
    iconButtonDanger: {
      "&:hover": { color: AEROS.error, borderColor: alpha(AEROS.error, 0.4) },
    },

    // ---- documentação ----
    docSection: {
      borderTop: `1px solid ${borderSoft}`,
      paddingTop: 22,
      marginTop: 22,
      display: "flex",
      flexDirection: "column",
      gap: 12,
    },
    docEyebrow: {
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: "0.14em",
      textTransform: "uppercase",
      color: stateText(theme, "#C08BFF", "purple"),
    },
    docTitle: {
      fontFamily: AEROS.fontDisplay,
      fontSize: 15,
      fontWeight: 700,
      letterSpacing: "-0.01em",
      marginTop: 4,
      color: theme.palette.text.primary,
    },
    docIntro: {
      fontSize: 12.5,
      color: theme.palette.text.secondary,
      marginTop: 2,
      maxWidth: 720,
      lineHeight: 1.5,
    },
    docTypeRow: { display: "flex", gap: 8, flexWrap: "wrap" },
    docText: {
      fontSize: 12.5,
      color: theme.palette.text.secondary,
      lineHeight: 1.55,
      maxWidth: 860,
    },

    // ---- painel de código ----
    codePanel: {
      maxWidth: 860,
      backgroundColor: CODE.bg,
      border: `1px solid ${CODE.border}`,
      borderRadius: 12,
      overflow: "hidden",
      fontFamily: MONO,
      fontSize: 12.5,
      lineHeight: 1.7,
    },
    codeHeader: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "8px 10px 8px 14px",
      borderBottom: `1px solid ${CODE.border}`,
      minWidth: 0,
    },
    methodBadge: {
      backgroundColor: alpha(AEROS.success, 0.15),
      color: AEROS.success,
      fontWeight: 700,
      fontSize: 11,
      letterSpacing: "0.08em",
      borderRadius: 5,
      padding: "2px 8px",
      fontFamily: theme.typography.fontFamily,
      flex: "none",
    },
    codeEndpoint: {
      flex: 1,
      minWidth: 0,
      color: CODE.text,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    codeCopy: {
      padding: 4,
      color: CODE.muted,
      flex: "none",
      "&:hover": { color: CODE.text, backgroundColor: "transparent" },
    },
    codeTabs: { display: "flex", gap: 4, padding: "8px 14px 0", flexWrap: "wrap" },
    codeTab: {
      background: "transparent",
      border: "none",
      cursor: "pointer",
      fontFamily: theme.typography.fontFamily,
      fontSize: 11.5,
      fontWeight: 600,
      color: CODE.muted,
      borderRadius: 6,
      padding: "3px 10px",
      "&:hover": { color: CODE.text },
      "&:focus-visible": { outline: `2px solid ${AEROS.cyan}`, outlineOffset: 1 },
    },
    codeTabActive: { backgroundColor: "rgba(255,255,255,0.08)", color: CODE.text },
    codeBody: {
      padding: "14px 16px",
      overflowX: "auto",
      color: CODE.text,
      ...theme.scrollbarStylesSoft,
    },
    codeLine: { whiteSpace: "pre" },
    codeKey: { color: CODE.key },
    codeComment: { color: CODE.muted },
  };
});

export default useIntegrationPageStyles;
